import { ReactNode, useEffect } from "react";
import useUrlParams from "../hooks/useUrlParams";
import useSocket from "../hooks/useSocket";
import useTrueTwin from "../hooks/useTrueTwin";
import useFiles from "../hooks/useFiles";
import useScenario from "../hooks/useScenario";
import { isOperateMode } from "../hooks/utils";
import { showToastMessage } from "../../../utils/commonFunctions";

interface FilesProviderProps {
    children: ReactNode;
}
const FilesProvider = ({ children }: FilesProviderProps) => {
    const { socket } = useSocket()
    const { truetwin_id } = useTrueTwin()
    const { getAllMinioStorageFiles, setAllACPStorageFiles, getAllOnboardFiles, setAllDownlinkingFiles, getDownlinkQueue, setAllOnboardFiles, getDownlinkRefreshTime } = useFiles()
    const { scenario_id, dashboard } = useUrlParams()
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)

    //GET GROUND FILES
    useEffect(() => {
        if (scenario_id && truetwin_id && operateMode) {
            getAllMinioStorageFiles(truetwin_id)
        }
    }, [operateMode, scenario_id, truetwin_id])

    //GET ONBOARD FILES
    useEffect(() => {
        if (scenario_id && truetwin_id && operateMode) {
            getAllOnboardFiles(truetwin_id)
            getDownlinkQueue(truetwin_id)
            getDownlinkRefreshTime()
        }
    }, [operateMode, scenario_id, truetwin_id])

    // LISTEN WEBSOCKET MESSAGE
    useEffect(() => {
        if (!scenario_id || !operateMode || !truetwin_id || !socket) return;
        const handleMessage = (event: MessageEvent) => {
            try {
                // PARSE THE INCOMING MESSAGE
                const parsed = JSON.parse(event.data);

                // CHECK IF THE EVENT IS FILE_UPLOAD_STATUS_UPDATE
                if (parsed?.event === "FILE_UPLOAD_STATUS_UPDATE" && parsed?.data) {
                    const { FileID, Progress, Status, FileType } = parsed.data;

                    // HANDLE UPLOAD FILE TYPE
                    if (FileType === 'upload') {
                        setAllACPStorageFiles((prev) => {
                            if (!prev) return prev;

                            // Find the folder that contains this FileID
                            const folderName = Object.keys(prev).find((folder) =>
                                prev[folder].has(FileID)
                            );

                            if (!folderName) return prev; // File not found in any folder

                            const folderMap = prev[folderName];

                            // Clone the folder's Map to avoid mutating state
                            const updatedFolderMap = new Map(folderMap);

                            const existingFile = updatedFolderMap.get(FileID);

                            if (existingFile && typeof existingFile === "object") {
                                updatedFolderMap.set(FileID, {
                                    ...existingFile,
                                    progress: Progress,
                                    fileStatus: Status,
                                });

                                // Show toast message if file upload is cancelled
                                if (Status === 'Cancelled') {
                                    const fileName = existingFile.name || existingFile.fileName || FileID;
                                    showToastMessage('info', `File uplink has been cancelled for ${fileName}`);
                                }
                            }

                            // Return updated object (clone prev + replace one folder)
                            return {
                                ...prev,
                                [folderName]: updatedFolderMap,
                            };
                        });

                        // HANDLE DOWNLOAD FILE TYPE
                    } else if (FileType === 'download') {
                        let oldFileObject;
                        // UPDATE DOWNLINKING FILES
                        setAllDownlinkingFiles((prev) => {
                            if (!prev || !prev.has(FileID)) return prev;

                            const updatedMap = new Map(prev);
                            const existingFile = updatedMap.get(FileID);

                            if (existingFile && typeof existingFile === "object") {
                                updatedMap.set(FileID, {
                                    ...existingFile,
                                    progress: Progress,
                                    status: Status,
                                });
                            }

                            return updatedMap;
                        });

                        // UPDATE ONBOARD FILES
                        setAllOnboardFiles((prev) => {
                            if (!prev) return prev;

                            // CLONE TOP-LEVEL RECORD ONLY IF NEEDED
                            let updatedRecord: Record<string, Map<string, any>> | null = null;

                            for (const [payloadKey, payloadMap] of Object.entries(prev)) {
                                if (payloadMap.has(FileID)) {
                                    const existingFile = payloadMap.get(FileID);
                                    oldFileObject = existingFile;
                                    if (existingFile && existingFile.status !== Status) {
                                        // ONLY NOW CLONE TOP-LEVEL RECORD AND INNER MAP
                                        updatedRecord = { ...prev };
                                        const updatedPayloadMap = new Map(payloadMap);
                                        updatedPayloadMap.set(FileID, { ...existingFile, status: Status });
                                        updatedRecord[payloadKey] = updatedPayloadMap;
                                    }

                                    // STOP LOOP ONCE FILE IS FOUND
                                    break;
                                }
                            }

                            // RETURN PREVIOUS STATE IF NOTHING CHANGED TO AVOID UNNECESSARY RERENDER
                            return updatedRecord ?? prev;
                        });

                        //THE CASE WHERE DOWNLINK COMPLTED AND CALL GET GROUND FILES API TO SEE IT IN DOWNLOADS FOLDER
                        if (Status === 'download_success' && oldFileObject?.status !== Status) {
                            getAllMinioStorageFiles(truetwin_id)
                        }

                    }
                }

            } catch (error) {
                // LOG ERROR IF PARSING FAILS
                console.error("Error parsing websocket message:", error, event.data);
            }
        };

        socket.addEventListener("message", handleMessage);

        return () => {
            socket.removeEventListener("message", handleMessage);
        };
    }, [scenario_id, operateMode, truetwin_id, socket]);

    return children

}

export default FilesProvider;